import React, { useCallback, useEffect, useState } from 'react';
import ZoomInRoundedIcon from '@mui/icons-material/ZoomInRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Header from '../components/header';
import '../styles/galeria.css';
import { useLanguage } from '../context/LanguageContext';

const loadGalleryImages = () => {
  const galleryContext = require.context('../image/galeria', false, /\.(png|jpe?g|webp|gif)$/i);

  return galleryContext
    .keys()
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
    .map((filePath) => {
      const importedImage = galleryContext(filePath);
      return {
        img: importedImage.default || importedImage
      };
    });
};

const galleryItems = loadGalleryImages();

const Galeria = () => {
  const { language } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(null);

  const content = {
    es: {
      eyebrow: 'Experiencia Sainza',
      title: 'Galeria viva',
      subtitle:
        'Un recorrido visual de nuestra comunidad y productos.',
      zoom: 'Ver grande',
      close: 'Cerrar galeria',
      photoAlt: 'Foto de galeria',
      thumbAria: 'Abrir miniatura'
    },
    en: {
      eyebrow: 'Sainza Experience',
      title: 'Living gallery',
      subtitle:
        'A visual journey through our community and products.',
      zoom: 'Open image',
      close: 'Close gallery',
      photoAlt: 'Gallery photo',
      thumbAria: 'Open thumbnail'
    }
  };

  const t = content[language];

  const activeItem = activeIndex === null ? null : galleryItems[activeIndex];
  const getAlt = (index) => `${t.photoAlt} ${index + 1}`;

  const openLightbox = useCallback((index) => setActiveIndex(index), []);
  const closeLightbox = useCallback(() => setActiveIndex(null), []);

  const goPrev = useCallback(() => {
    setActiveIndex((current) => {
      if (current === null) {
        return 0;
      }
      return (current - 1 + galleryItems.length) % galleryItems.length;
    });
  }, []);

  const goNext = useCallback(() => {
    setActiveIndex((current) => {
      if (current === null) {
        return 0;
      }
      return (current + 1) % galleryItems.length;
    });
  }, []);

  useEffect(() => {
    if (activeIndex !== null && activeIndex > galleryItems.length - 1) {
      setActiveIndex(0);
    }
  }, [activeIndex]);

  useEffect(() => {
    if (activeIndex === null) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeLightbox();
      }
      if (event.key === 'ArrowLeft') {
        goPrev();
      }
      if (event.key === 'ArrowRight') {
        goNext();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeIndex, closeLightbox, goPrev, goNext]);

  return (
    <div className="galeria-container page-shell">
      <Header />
      <section className="galeria-shell">
        <header className="galeria-header">
          <p className="section-eyebrow">{t.eyebrow}</p>
          <h1 className="galeria-title">{t.title}</h1>
          <p className="galeria-subtitle">{t.subtitle}</p>
        </header>

        <div className="galeria-grid">
          {galleryItems.map((item, index) => (
            <button
              type="button"
              key={`${item.img}-${index}`}
              className="gallery-card"
              onClick={() => openLightbox(index)}
              style={{ '--delay': `${index * 80}ms` }}
              aria-label={`${t.zoom} ${index + 1}`}
            >
              <img src={item.img} alt={getAlt(index)} loading="lazy" className="gallery-image" />
              <span className="gallery-card-overlay" aria-hidden="true">
                <ZoomInRoundedIcon fontSize="inherit" />
              </span>
            </button>
          ))}
        </div>
      </section>

      {activeItem && (
        <div className="gallery-lightbox" role="dialog" aria-modal="true" onClick={closeLightbox}>
          <button
            type="button"
            className="lightbox-close"
            onClick={closeLightbox}
            aria-label={t.close}
          >
            <CloseRoundedIcon />
          </button>

          <div className="lightbox-stage" onClick={(event) => event.stopPropagation()}>
            <img src={activeItem.img} alt={getAlt(activeIndex)} className="lightbox-image" />

            <div className="lightbox-strip">
              {galleryItems.map((item, index) => (
                <button
                  type="button"
                  key={`${item.img}-thumb`}
                  className={`lightbox-thumb ${index === activeIndex ? 'active' : ''}`}
                  onClick={() => setActiveIndex(index)}
                  aria-label={`${t.thumbAria} ${index + 1}`}
                >
                  <img src={item.img} alt={getAlt(index)} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Galeria;
